import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../utils/firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp 
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * ClubCreator - Platform owner tool to pre-create clubs for demos
 * 
 * Creates clubs with null owner fields that can be claimed via existing join flow.
 * Only accessible to users with platformOwner: true
 * 
 * Flow:
 * 1. Create club here → get club code (e.g., "SAL123")
 * 2. Set up pitches, teams, allocations by switching activeClubId
 * 3. Give code to real admin
 * 4. They sign up and "Join existing club" with the code
 * 5. Use "Transfer Ownership" button here to make them the owner/admin
 */

const ClubCreator = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isPlatformOwner, setIsPlatformOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Form state
  const [clubName, setClubName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdCode, setCreatedCode] = useState(null);
  
  // Pending clubs
  const [pendingClubs, setPendingClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(true);
  
  // All clubs for switcher
  const [allClubs, setAllClubs] = useState([]);
  const [switching, setSwitching] = useState(false);
  
  // Transfer ownership modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [clubMembers, setClubMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [transferring, setTransferring] = useState(false);

  // Check if user is platform owner
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);
        const userDoc = await getDoc(doc(db, 'users', authUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          if (data.platformOwner === true) {
            setIsPlatformOwner(true);
            loadPendingClubs();
            loadAllClubs();
          } else {
            setIsPlatformOwner(false);
          }
        }
      } else {
        setUser(null);
        setUserData(null);
        setIsPlatformOwner(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load clubs with pending_owner status
  const loadPendingClubs = async () => {
    setLoadingClubs(true);
    try {
      const clubsSnapshot = await getDocs(collection(db, 'clubs'));
      const pending = [];
      clubsSnapshot.forEach((clubDoc) => {
        const data = clubDoc.data();
        if (data.status === 'pending_owner') {
          pending.push({ id: clubDoc.id, ...data });
        }
      });
      setPendingClubs(pending);
    } catch (error) {
      console.error('Error loading pending clubs:', error);
    }
    setLoadingClubs(false);
  };

  // Load all clubs for the switcher dropdown
  const loadAllClubs = async () => {
    try {
      const clubsSnapshot = await getDocs(collection(db, 'clubs'));
      const clubs = [];
      clubsSnapshot.forEach((clubDoc) => {
        const data = clubDoc.data();
        clubs.push({ id: clubDoc.id, ...data });
      });
      // Sort by name
      clubs.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setAllClubs(clubs);
    } catch (error) {
      console.error('Error loading all clubs:', error);
    }
  };

  // Switch to a different club (changes actual clubId)
  const handleSwitchClub = async (newClubId) => {
    if (!user || switching || !userData) return;
    
    setSwitching(true);
    try {
      // Store home club if not already stored
      const updateData = {
        clubId: newClubId
      };
      
      // If this is the first switch, save the original clubId as homeClubId
      if (!userData.homeClubId) {
        updateData.homeClubId = userData.clubId;
      }
      
      await updateDoc(doc(db, 'users', user.uid), updateData);
      
      // Find club name for confirmation
      const club = allClubs.find(c => c.id === newClubId);
      alert(`Switched to: ${club?.name || newClubId}\n\nThe app will now reload.`);
      
      // Reload the page to apply the change globally
      window.location.href = '/menu';
      
    } catch (error) {
      console.error('Error switching club:', error);
      alert('Failed to switch club: ' + error.message);
    }
    setSwitching(false);
  };

  // Return to home club
  const handleReturnHome = async () => {
    if (!user || switching || !userData?.homeClubId) return;
    
    setSwitching(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        clubId: userData.homeClubId
      });
      
      const homeClub = allClubs.find(c => c.id === userData.homeClubId);
      alert(`Returned to: ${homeClub?.name || userData.homeClubId}\n\nThe app will now reload.`);
      
      window.location.href = '/menu';
      
    } catch (error) {
      console.error('Error returning home:', error);
      alert('Failed to return home: ' + error.message);
    }
    setSwitching(false);
  };

  // Generate club ID (same format as existing signup - 6 uppercase alphanumeric)
  const generateClubId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ0123456789';
    let id = '';
    for (let i = 0; i < 6; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return id;
  };

  // Create a new club with pending owner status
  const handleCreateClub = async () => {
    if (!clubName.trim()) {
      alert('Please enter a club name');
      return;
    }

    setCreating(true);
    try {
      const clubId = generateClubId();
      
      // Calculate trial end date (7 days from now)
      const now = new Date();
      const trialEndsAt = new Date(now);
      trialEndsAt.setDate(trialEndsAt.getDate() + 7);

      await setDoc(doc(db, 'clubs', clubId), {
        // Club identity - matches existing pattern
        name: clubName.trim(),
        clubId: clubId,
        code: clubId, // Same as clubId for join flow
        
        // Status - marks this as awaiting real owner
        status: 'pending_owner',
        
        // Creator info (platform owner, not the eventual owner)
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        
        // Owner fields - null until transferred
        ownerId: null,
        ownerFirstName: null,
        ownerSurname: null,
        ownerName: null,
        ownerMobile: null,
        
        // Admin fields - null until transferred
        adminEmail: null,
        adminFirstName: null,
        adminSurname: null,
        adminFullName: null,
        adminMobile: null,
        
        // Setup state
        hasCompletedSetup: false,
        facilityConfigured: false,
        facilityCount: 0,
        
        // Subscription - matches existing signup defaults
        subscriptionTier: 'premium',
        subscriptionStatus: 'trial',
        trialEndsAt: trialEndsAt.toISOString(),
        
        // Image defaults
        imageHeight: null,
        imageWidth: null,
        
        // Other defaults
        configurationMethod: null,
        lastBackupDate: null,
      });

      setCreatedCode(clubId);
      setClubName('');
      loadPendingClubs();
      
    } catch (error) {
      console.error('Error creating club:', error);
      alert('Failed to create club: ' + error.message);
    }
    setCreating(false);
  };

  // Delete a pending club
  const handleDeleteClub = async (clubId) => {
    if (!window.confirm('Are you sure you want to delete this pending club? This cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'clubs', clubId));
      loadPendingClubs();
    } catch (error) {
      console.error('Error deleting club:', error);
      alert('Failed to delete club: ' + error.message);
    }
  };

  // Load members of a club for ownership transfer
  const loadClubMembers = async (club) => {
    setSelectedClub(club);
    setShowTransferModal(true);
    setLoadingMembers(true);
    setClubMembers([]);
    
    try {
      const usersQuery = query(
        collection(db, 'users'),
        where('clubId', '==', club.clubId)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      const members = [];
      usersSnapshot.forEach((userDoc) => {
        const userData = userDoc.data();
        // Don't include platform owner in the list
        if (userDoc.id !== user.uid) {
          members.push({
            id: userDoc.id,
            ...userData
          });
        }
      });
      
      setClubMembers(members);
    } catch (error) {
      console.error('Error loading club members:', error);
      alert('Failed to load club members: ' + error.message);
    }
    setLoadingMembers(false);
  };

  // Transfer ownership to a member
  const handleTransferOwnership = async (member) => {
    if (!window.confirm(`Transfer ownership of "${selectedClub.name}" to ${member.fullName || member.email}? They will become the club admin.`)) {
      return;
    }

    setTransferring(true);
    try {
      // Update the club document with new owner info
      await updateDoc(doc(db, 'clubs', selectedClub.clubId), {
        status: 'active',
        ownerId: member.id,
        ownerFirstName: member.firstName || null,
        ownerSurname: member.surname || null,
        ownerName: member.fullName || `${member.firstName} ${member.surname}`,
        ownerMobile: member.mobile || null,
        adminEmail: member.email,
        adminFirstName: member.firstName || null,
        adminSurname: member.surname || null,
        adminFullName: member.fullName || `${member.firstName} ${member.surname}`,
        adminMobile: member.mobile || null,
      });

      // Update the user's role to admin
      await updateDoc(doc(db, 'users', member.id), {
        role: 'admin',
        roleChangedAt: new Date().toISOString(),
      });

      alert(`Ownership transferred successfully! ${member.fullName || member.email} is now the admin of ${selectedClub.name}.`);
      setShowTransferModal(false);
      setSelectedClub(null);
      loadPendingClubs();
      
    } catch (error) {
      console.error('Error transferring ownership:', error);
      alert('Failed to transfer ownership: ' + error.message);
    }
    setTransferring(false);
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  // Loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  // Not authorized
  if (!isPlatformOwner) {
    return (
      <div style={styles.container}>
        <div style={styles.unauthorized}>
          <h2>🔒 Access Denied</h2>
          <p>This page is only accessible to platform owners.</p>
          <button onClick={() => navigate('/menu')} style={styles.backButton}>
            ← Back to Menu
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate('/owner-9x7kq2m')} style={styles.backLink}>
          ← Back to Owner Dashboards
        </button>
        <h1 style={styles.title}>🏟️ Club Creator</h1>
        <p style={styles.subtitle}>Pre-create clubs for demos and hand over to real admins</p>
      </div>

      {/* Club Switcher - ONLY for platform owner */}
      <div style={styles.switcherCard}>
        <div style={styles.switcherHeader}>
          <h2 style={styles.switcherTitle}>🔄 Active Club</h2>
          <span style={styles.switcherBadge}>Platform Owner Mode</span>
        </div>
        <p style={styles.switcherDescription}>
          Switch between clubs to set up demos. Your actual clubId will change.
        </p>
        <div style={styles.switcherControls}>
          <select
            value={userData?.clubId || ''}
            onChange={(e) => handleSwitchClub(e.target.value)}
            disabled={switching}
            style={styles.switcherSelect}
          >
            <option value="" disabled>Select a club...</option>
            {allClubs.map((club) => (
              <option key={club.id} value={club.id}>
                {club.name} ({club.id}) {club.status === 'pending_owner' ? '⏳' : ''}
              </option>
            ))}
          </select>
          {userData?.homeClubId && userData?.clubId !== userData?.homeClubId && (
            <button
              onClick={handleReturnHome}
              disabled={switching}
              style={styles.returnHomeButton}
            >
              🏠 Return Home
            </button>
          )}
          {switching && <span style={styles.switchingText}>Switching...</span>}
        </div>
        {userData?.clubId && (
          <div style={styles.currentClubInfo}>
            <strong>Currently working in:</strong>{' '}
            {allClubs.find(c => c.id === userData.clubId)?.name || userData.clubId}
            {userData.homeClubId && userData.clubId !== userData.homeClubId && (
              <span style={styles.notYourClubBadge}>
                (Home: {allClubs.find(c => c.id === userData.homeClubId)?.name || userData.homeClubId})
              </span>
            )}
          </div>
        )}
      </div>

      {/* Success message */}
      {createdCode && (
        <div style={styles.successBanner}>
          <div style={styles.successIcon}>✅</div>
          <div style={styles.successContent}>
            <strong>Club created successfully!</strong>
            <p style={styles.successText}>
              Share this club code with the new admin:
            </p>
            <div style={styles.codeDisplay}>
              <span style={styles.codeText}>{createdCode}</span>
              <button 
                onClick={() => copyToClipboard(createdCode)}
                style={styles.copyButton}
              >
                Copy
              </button>
            </div>
            <p style={styles.successHint}>
              They'll use this when signing up → "Join existing club"
            </p>
          </div>
          <button 
            onClick={() => setCreatedCode(null)} 
            style={styles.dismissButton}
          >
            ×
          </button>
        </div>
      )}

      {/* Create new club form */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Create New Club</h2>
        <div style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Club Name</label>
            <input
              type="text"
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              placeholder="e.g., Salisbury United FC"
              style={styles.input}
            />
          </div>
          <button
            onClick={handleCreateClub}
            disabled={creating || !clubName.trim()}
            style={{
              ...styles.createButton,
              opacity: creating || !clubName.trim() ? 0.6 : 1,
            }}
          >
            {creating ? 'Creating...' : 'Create Club'}
          </button>
        </div>
      </div>

      {/* Pending clubs list */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Pending Clubs (Awaiting Owner)</h2>
        
        {loadingClubs ? (
          <p style={styles.loadingText}>Loading...</p>
        ) : pendingClubs.length === 0 ? (
          <p style={styles.emptyText}>No pending clubs. Create one above!</p>
        ) : (
          <div style={styles.clubList}>
            {pendingClubs.map((club) => (
              <div key={club.id} style={styles.clubItem}>
                <div style={styles.clubInfo}>
                  <h3 style={styles.clubName}>{club.name}</h3>
                  <div style={styles.clubMeta}>
                    <span style={styles.metaItem}>
                      <strong>Club Code:</strong>{' '}
                      <code style={styles.inlineCode}>{club.clubId}</code>
                      <button
                        onClick={() => copyToClipboard(club.clubId)}
                        style={styles.smallCopyButton}
                      >
                        Copy
                      </button>
                    </span>
                    <span style={styles.metaItem}>
                      <strong>Created:</strong>{' '}
                      {club.createdAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                    </span>
                  </div>
                </div>
                <div style={styles.clubActions}>
                  <button
                    onClick={() => loadClubMembers(club)}
                    style={styles.transferButton}
                  >
                    Transfer Ownership
                  </button>
                  <button
                    onClick={() => handleDeleteClub(club.id)}
                    style={styles.deleteButton}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>How It Works</h2>
        <ol style={styles.instructionList}>
          <li><strong>Create</strong> a club above → you get a 6-character code</li>
          <li><strong>Switch</strong> to that club by setting <code style={styles.inlineCode}>activeClubId</code> on your user doc in Firebase</li>
          <li><strong>Set up</strong> pitches, teams, allocations for the demo</li>
          <li><strong>Share</strong> the club code with the real admin</li>
          <li>They <strong>sign up</strong> and choose "Join existing club" with the code</li>
          <li>Click <strong>"Transfer Ownership"</strong> above to make them the admin</li>
        </ol>
      </div>

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Transfer Ownership</h2>
              <button 
                onClick={() => {
                  setShowTransferModal(false);
                  setSelectedClub(null);
                }}
                style={styles.modalClose}
              >
                ×
              </button>
            </div>
            
            <p style={styles.modalSubtitle}>
              Select a member of <strong>{selectedClub?.name}</strong> to become the new owner/admin:
            </p>

            {loadingMembers ? (
              <p style={styles.loadingText}>Loading members...</p>
            ) : clubMembers.length === 0 ? (
              <div style={styles.noMembers}>
                <p>No members have joined this club yet.</p>
                <p style={styles.noMembersHint}>
                  Share the club code <code style={styles.inlineCode}>{selectedClub?.clubId}</code> with 
                  the new admin. Once they sign up and join, they'll appear here.
                </p>
              </div>
            ) : (
              <div style={styles.memberList}>
                {clubMembers.map((member) => (
                  <div key={member.id} style={styles.memberItem}>
                    <div style={styles.memberInfo}>
                      <strong>{member.fullName || `${member.firstName} ${member.surname}`}</strong>
                      <span style={styles.memberEmail}>{member.email}</span>
                      <span style={styles.memberRole}>Current role: {member.role}</span>
                    </div>
                    <button
                      onClick={() => handleTransferOwnership(member)}
                      disabled={transferring}
                      style={{
                        ...styles.makeAdminButton,
                        opacity: transferring ? 0.6 : 1,
                      }}
                    >
                      {transferring ? 'Transferring...' : 'Make Admin'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '32px 24px',
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
  },
  header: {
    marginBottom: '24px',
  },
  backLink: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '0',
    marginBottom: '16px',
    display: 'block',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#243665',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  // Club Switcher styles
  switcherCard: {
    backgroundColor: '#fef3c7',
    border: '2px solid #f59e0b',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
  },
  switcherHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  switcherTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#92400e',
    margin: 0,
  },
  switcherBadge: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#92400e',
    backgroundColor: '#fde68a',
    padding: '4px 8px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  switcherDescription: {
    fontSize: '13px',
    color: '#92400e',
    margin: '0 0 12px 0',
  },
  switcherControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  switcherSelect: {
    flex: 1,
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d97706',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
  },
  switchingText: {
    fontSize: '13px',
    color: '#92400e',
    fontStyle: 'italic',
  },
  returnHomeButton: {
    padding: '10px 16px',
    backgroundColor: '#243665',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  currentClubInfo: {
    marginTop: '12px',
    fontSize: '13px',
    color: '#92400e',
    padding: '8px 12px',
    backgroundColor: 'rgba(255,255,255,0.5)',
    borderRadius: '6px',
  },
  notYourClubBadge: {
    marginLeft: '8px',
    fontSize: '11px',
    color: '#dc2626',
    fontWeight: '500',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: '#64748b',
  },
  unauthorized: {
    textAlign: 'center',
    padding: '48px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
  },
  backButton: {
    marginTop: '16px',
    padding: '10px 20px',
    backgroundColor: '#243665',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  successBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    backgroundColor: '#ecfdf5',
    border: '1px solid #10b981',
    borderRadius: '12px',
    marginBottom: '24px',
  },
  successIcon: {
    fontSize: '24px',
  },
  successContent: {
    flex: 1,
  },
  successText: {
    margin: '8px 0',
    color: '#047857',
    fontSize: '14px',
  },
  successHint: {
    margin: '8px 0 0 0',
    color: '#047857',
    fontSize: '13px',
    fontStyle: 'italic',
  },
  codeDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '8px',
  },
  codeText: {
    fontSize: '24px',
    fontWeight: '700',
    fontFamily: 'monospace',
    color: '#065f46',
    letterSpacing: '2px',
  },
  copyButton: {
    padding: '6px 12px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  dismissButton: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0',
    lineHeight: 1,
  },
  card: {
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 16px 0',
  },
  form: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-end',
  },
  inputGroup: {
    flex: 1,
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  createButton: {
    padding: '12px 24px',
    backgroundColor: '#243665',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  loadingText: {
    color: '#6b7280',
    fontStyle: 'italic',
  },
  emptyText: {
    color: '#6b7280',
    fontStyle: 'italic',
  },
  clubList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  clubItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    flexWrap: 'wrap',
    gap: '12px',
  },
  clubInfo: {
    flex: 1,
    minWidth: '200px',
  },
  clubName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 8px 0',
  },
  clubMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    fontSize: '13px',
    color: '#6b7280',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  inlineCode: {
    backgroundColor: '#f1f5f9',
    padding: '2px 6px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '13px',
  },
  smallCopyButton: {
    marginLeft: '6px',
    padding: '2px 8px',
    backgroundColor: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '11px',
  },
  clubActions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  transferButton: {
    padding: '8px 16px',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
  },
  deleteButton: {
    padding: '8px 16px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  instructionList: {
    margin: 0,
    paddingLeft: '20px',
    color: '#4b5563',
    lineHeight: 2,
  },
  // Modal styles
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '80vh',
    overflow: 'auto',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  modalClose: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0',
    lineHeight: 1,
  },
  modalSubtitle: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '20px',
  },
  noMembers: {
    padding: '20px',
    backgroundColor: '#fef3c7',
    borderRadius: '8px',
    textAlign: 'center',
  },
  noMembersHint: {
    fontSize: '13px',
    color: '#92400e',
    marginTop: '8px',
  },
  memberList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  memberItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  memberInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  memberEmail: {
    fontSize: '13px',
    color: '#6b7280',
  },
  memberRole: {
    fontSize: '12px',
    color: '#9ca3af',
  },
  makeAdminButton: {
    padding: '8px 16px',
    backgroundColor: '#243665',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
  },
};

export default ClubCreator;
